import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SiteNav } from "@/components/SiteNav";
import { ConciergeChat } from "@/components/ConciergeChat";
import { BookingForm } from "@/components/BookingForm";
import {
  BRAND, HERO_VIDEO, HERO_POSTER, PBAND_IMAGE, REEL_IMAGES, REEL_IMAGES_2, STICKERS,
  ARTIST_HERO, ARTIST_CARDS, SPACES, WALL, EVENTS, SYNC_IMAGE, TICKER_A, TICKER_B, STUDIO_PHOTOS,
} from "@/data/content";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

// Parallax image — shifts vertically as it scrolls through the viewport
const ParallaxImg = ({ src, alt = "", className = "pi", range = 60 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-range, range]);
  return (
    <motion.img ref={ref} src={src} alt={alt} className={className} style={{ y }} loading="lazy" />
  );
};

const heroLetter = {
  hidden: { y: "110%", opacity: 0 },
  show: (i) => ({ y: "0%", opacity: 1, transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const cursorRef = useRef(null);
  const tracksRef = useRef(null);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const els = document.querySelectorAll(".reveal, .reveal-x, .reveal-pop");
    if (!("IntersectionObserver" in window)) { els.forEach((el) => el.classList.add("in")); return; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const c = cursorRef.current;
    if (!c || window.matchMedia("(pointer: coarse)").matches) return;
    const move = (e) => { c.style.left = `${e.clientX}px`; c.style.top = `${e.clientY}px`; c.style.opacity = "1"; };
    const over = (e) => c.classList.toggle("big", !!e.target.closest("a,button,.space-slide,.mosaic-item,.wall-item,.reel-track img"));
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", over);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseover", over); };
  }, []);

  useEffect(() => {
    const el = tracksRef.current;
    if (!el) return;
    let down = false, sx = 0, sl = 0;
    const ds = (e) => { down = true; sx = e.pageX; sl = el.scrollLeft; el.classList.add("grabbing"); };
    const dm = (e) => { if (!down) return; el.scrollLeft = sl - (e.pageX - sx); };
    const de = () => { down = false; el.classList.remove("grabbing"); };
    el.addEventListener("mousedown", ds);
    window.addEventListener("mousemove", dm);
    window.addEventListener("mouseup", de);
    return () => { el.removeEventListener("mousedown", ds); window.removeEventListener("mousemove", dm); window.removeEventListener("mouseup", de); };
  }, []);

  useEffect(() => {
    const track = document.querySelector(".studios-track");
    if (!track) return;
    const slides = track.querySelectorAll(".studio-slide");
    if (!slides.length || !("IntersectionObserver" in window)) {
      slides.forEach((s) => s.classList.add("snapped"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.target.classList.toggle("snapped", e.intersectionRatio >= 0.65)),
      { root: track, threshold: [0, 0.35, 0.65, 0.85, 1] }
    );
    slides.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  const tickerA = [...TICKER_A, ...TICKER_A];
  const tickerB = [...TICKER_B, ...TICKER_B];
  const reel = [...REEL_IMAGES, ...REEL_IMAGES];
  const reel2 = [...REEL_IMAGES_2, ...REEL_IMAGES_2];
  const words = [
    { t: "Create.", c: "c1" },
    { t: "Record.", c: "c2" },
    { t: "Design.", c: "c3" },
  ];

  return (
    <>
      <div className="site-mesh" />
      <div className="grain" />
      <div className="cursor" ref={cursorRef} />
      <motion.div className="scroll-progress" style={{ scaleX: scrollYProgress }} data-testid="scroll-progress" />
      <SiteNav show={true} />

      <main id="top">
        {/* HERO — background video, block letters ABOVE */}
        <section className="hero hero-video-mode">
          <video
            className="hero-video"
            src={HERO_VIDEO}
            poster={HERO_POSTER}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            data-testid="hero-bg-video"
          />
          <div className="hero-video-tint" aria-hidden="true" />
          <div className="hero-inner">
            <div style={{ textAlign: "center" }}>
              <motion.span
                className="hero-tag"
                initial={{ opacity: 0, y: -12, rotate: -2 }}
                animate={{ opacity: 1, y: 0, rotate: -2 }}
                transition={{ duration: 0.6 }}
              >
                1840 S Sepulveda · West LA · {BRAND.venueName}
              </motion.span>
            </div>

            <h1 className="hero-type" data-testid="hero-headline">
              {words.map((w, i) => (
                <span className="l" key={w.t} style={{ overflow: "hidden", display: "block" }}>
                  <motion.span
                    className={w.c}
                    style={{ display: "inline-block" }}
                    variants={heroLetter}
                    custom={i}
                    initial="hidden"
                    animate="show"
                  >
                    {w.t}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.div
              className="hero-cta-row"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <a className="btn btn-fill" href="#contact" data-testid="hero-book" onClick={(e) => { e.preventDefault(); scrollTo("contact"); }}>Book the campus</a>
              <a className="btn btn-line" href="#studios" data-testid="hero-rooms" onClick={(e) => { e.preventDefault(); scrollTo("studios"); }}>See the studios</a>
              <div className="eq" aria-hidden="true"><span /><span /><span /><span /><span /><span /><span /></div>
            </motion.div>

            <p className="hero-lead reveal in">
              A recording studio, a courtyard, a bar and a villa — one Tuscan campus in West LA where
              the red light goes on at 2pm and the party starts at 8.
            </p>
          </div>
        </section>

        {/* TICKER */}
        <div className="ticker-wrap bright"><div className="ticker">{tickerA.map((t, i) => <span key={i}>{t} <span className="dot">✦</span></span>)}</div></div>

        {/* DUAL REEL */}
        <div className="reels">
          <div className="reel"><div className="reel-track">{reel.map((s, i) => <img key={i} src={s} alt="" />)}</div></div>
          <div className="reel"><div className="reel-track rev">{reel2.map((s, i) => <img key={i} src={s} alt="" />)}</div></div>
        </div>

        {/* STUDIOS — horizontal scroll carousel of the real rooms */}
        <section className="studios" id="studios">
          <div className="section-head">
            <p className="eyebrow reveal">The studios</p>
            <h2 className="big reveal" style={{ "--d": ".1s" }}>Where the red light <em>turns on</em></h2>
            <p className="section-lead reveal" style={{ "--d": ".2s" }}>
              Purpose-built rooms for tracking, mixing and Dolby Atmos — warm acoustics, pro gear,
              California light through every window.
            </p>
          </div>
          <div className="studios-track" data-testid="studios-track">
            {STUDIO_PHOTOS.map((s, i) => (
              <motion.article
                key={i}
                className="studio-slide"
                data-testid={`studio-slide-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6 }}
              >
                <span className="studio-tag">{s.tag}</span>
                <img src={s.url} alt={`${s.name} — ${s.tag}`} loading="lazy" />
                <h3 className="studio-big-label" aria-label={s.name} data-testid={`studio-label-${i}`}>
                  {s.name.split("").map((ch, j) => (
                    <span key={j} className="sbl-ch" style={{ "--i": j }}>
                      {ch === " " ? "\u00A0" : ch}
                    </span>
                  ))}
                </h3>
                <div className="studio-cap">
                  <span className="studio-num">0{i + 1}</span>
                  <p>{s.tag}</p>
                </div>
              </motion.article>
            ))}
          </div>
          <p className="scroll-hint">← drag / scroll horizontally →</p>
        </section>

        {/* PARALLAX FEATURE BAND */}
        <section className="pband">
          <ParallaxImg src={PBAND_IMAGE} alt="The courtyard at night" range={90} />
          <div className="pband-cap">
            <motion.h2 initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              String lights.<br /><span className="y">Open sky.</span>
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}>
              A courtyard built for listening parties, launches and celebrations under the West LA night.
            </motion.p>
          </div>
        </section>

        {/* VIBE */}
        <section className="vibe">
          <div className="vibe-inner">
            <h2 className="reveal">Not just a <span className="hl">website</span>.<br />A place you want to show up.</h2>
            <p className="vibe-copy reveal" style={{ "--d": ".1s" }}>
              Behind the stucco walls: <strong>multiple recording rooms</strong>, a courtyard that
              fits a hundred, a <strong>built-in bar</strong>, Italian arches and California light.
              Artists track albums here. Couples say "I do" here. The industry shows up here.
            </p>
            <div className="stickers">
              {STICKERS.map((s, i) => (
                <motion.span
                  key={i}
                  className={`sticker ${s.cls}`}
                  initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
                  whileInView={{ opacity: 1, scale: 1, rotate: [-4, 4, -2][i % 3] }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 300, damping: 16, delay: i * 0.07 }}
                  whileHover={{ rotate: 0, scale: 1.1 }}
                >
                  {s.text}
                </motion.span>
              ))}
            </div>
          </div>
        </section>

        {/* ARTISTS */}
        <section className="artists" id="artists">
          <div className="artist-hero">
            <div className="artist-hero-img reveal-x">
              <ParallaxImg src={ARTIST_HERO} alt="Jacqueline Fernandez" range={40} />
            </div>
            <div className="artist-hero-copy">
              <p className="eyebrow reveal">Our artists</p>
              <h2 className="artist-name reveal" style={{ "--d": ".08s" }}>Jackie<span className="accent">Fernandez</span></h2>
              <p className="reveal" style={{ "--d": ".16s" }}>
                Bollywood icon, global citizen, sync-ready. Over a decade in Indian cinema: films,
                stage, music videos and a voice that travels across territories and platforms.
              </p>
              <p className="subtle reveal" style={{ "--d": ".22s" }}>
                Myst Music represents established performers and songwriters for sync licensing,
                collaboration and original production.
              </p>
            </div>
          </div>
          <div className="artist-row">
            {ARTIST_CARDS.map((a, i) => (
              <motion.article key={i} className={`artist-card reveal-x ${i === 1 ? "right" : ""}`} data-testid={`artist-card-${i}`} whileHover={{ y: -6 }}>
                <img src={a.url} alt={a.name} loading="lazy" />
                <div><h3>{a.name}</h3><p>{a.note}</p></div>
              </motion.article>
            ))}
          </div>
        </section>

        {/* TICKER */}
        <div className="ticker-wrap pink"><div className="ticker rev">{tickerB.map((t, i) => <span key={i}>{t} <span className="dot">★</span></span>)}</div></div>

        {/* SPACES */}
        <section className="spaces" id="spaces">
          <div className="section-head">
            <p className="eyebrow reveal">The campus</p>
            <h2 className="big reveal" style={{ "--d": ".1s" }}>Beyond <span className="stroke">the booth</span></h2>
          </div>
          <div className="spaces-track" ref={tracksRef} data-testid="spaces-track">
            {SPACES.map((s, i) => (
              <article className="space-slide" key={i} data-testid={`space-slide-${i}`}>
                <span className="tag">{s.tag}</span>
                <img src={s.url} alt={s.title} loading="lazy" />
                <div className="cap"><h3>{s.title}</h3><p>{s.body}</p></div>
              </article>
            ))}
          </div>
          <p className="scroll-hint">← drag to explore →</p>
        </section>

        {/* PHOTO WALL */}
        <section className="wall">
          <h2 className="reveal">Every corner<span>is a photo op</span></h2>
          <div className="wall-grid">
            {WALL.map((w, i) => (
              <motion.div
                key={i}
                className={`wall-item ${w.cls}`}
                data-testid={`wall-item-${i}`}
                initial={{ opacity: 0, scale: 0.8, rotate: [-6, 5, -3, 4, -2, 3][i] }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ type: "spring", stiffness: 200, damping: 20, delay: i * 0.06 }}
              >
                <img src={w.url} alt="" loading="lazy" />
              </motion.div>
            ))}
          </div>
        </section>

        {/* EVENTS */}
        <section className="events" id="events">
          <h2 className="reveal">Music and events,<br /><span className="g">same address.</span></h2>
          <div className="event-row">
            {EVENTS.map((ev, i) => (
              <div key={i} className="event-chip reveal-x" style={{ "--d": `${i * 0.05}s` }} data-testid={`event-chip-${i}`}>
                <strong>{ev.title}</strong><span>{ev.note}</span>
              </div>
            ))}
          </div>
        </section>

        {/* SYNC */}
        <section className="sync" id="music">
          <div className="sync-visual reveal-x"><ParallaxImg src={SYNC_IMAGE} alt="Highgate offices upstairs" range={40} /></div>
          <div className="reveal-x right">
            <h2>Oh — and <em>sync licensing</em></h2>
            <p>
              Myst Music's catalog and roster live here too. Global artists, one-stop clearance and
              original scores for picture. Highgate Entertainment is upstairs — the business happens
              in the same building you just fell in love with.
            </p>
            <a className="sync-link" href={`mailto:${BRAND.email}`} data-testid="sync-email">Talk sync →</a>
          </div>
        </section>

        {/* CONTACT */}
        <section className="contact" id="contact">
          <div className="contact-boom reveal">LET'S<br />GO</div>
          <h2 className="contact-title reveal" style={{ "--d": ".1s" }}>Let's make something <span className="accent">loud.</span></h2>
          <p className="contact-sub reveal" style={{ "--d": ".16s" }}>Book a session. Plan an event. Come walk the campus.</p>
          <BookingForm />
          <a className="contact-email reveal" style={{ "--d": ".4s" }} href={`mailto:${BRAND.email}`} data-testid="contact-email">{BRAND.email}</a>
          <p className="contact-addr reveal" style={{ "--d": ".46s" }}>
            {BRAND.address}<br />aka <a href={BRAND.venueUrl} target="_blank" rel="noopener noreferrer">{BRAND.venueName}</a>
          </p>
        </section>

        <footer className="footer">© {new Date().getFullYear()} {BRAND.name} · {BRAND.venueName} · West Los Angeles</footer>
      </main>
      <ConciergeChat />
    </>
  );
}
